//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import moment from "moment";
import { Granularity } from "../../../api/usage/v1/usage";

// The three ranges offered to the customer. These mirror the V1 credits page
// (CreditUsageRateGraph.tsx) so the two pages behave the same way.
export type UsageRangeKey = "MONTH_1" | "MONTH_3" | "YEAR_1";

// startOfUTCDay truncates a timestamp to midnight UTC.
//
// UTC, not local time: usage buckets are UTC days because starts_at is stored as
// a UTC ISO string. Truncating in local time gives a viewer in UTC+13 an
// off-by-one day and an empty trailing bar.
export const startOfUTCDay = (d: Date): Date => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

// subtractCalendar subtracts a whole number of calendar months/years from a
// whole UTC day boundary, clamping into the shorter month instead of
// overflowing into it: e.g. 2026-03-31 minus 1 month is 2026-02-28, not
// 2026-03-03. Date.prototype.setUTCMonth does NOT clamp - it overflows into
// the following month - so it silently narrowed the requested range (and, in
// a billing view, silently dropped several days of usage) whenever `to` fell
// on a day past the target month's last day. moment does clamp correctly
// here (verified for both the MONTH_1 and MONTH_3 cases above).
//
// Built from `to.getTime()`, not the `to` Date object itself: moment.utc()
// given a native Date normally reads it via valueOf()/getTime() rather than
// its local getters, but going through getTime() explicitly removes any
// dependency on that being true, consistent with startOfUTCDay's own
// UTC-getters-only approach above.
const subtractCalendar = (to: Date, amount: number, unit: "month" | "year"): Date => moment.utc(to.getTime()).subtract(amount, unit).toDate();

// usageRangeFor turns a selected range into the request window and bucket size.
//
// `to` is the start of the current UTC day, EXCLUSIVE, so today is never
// requested: everestd only sets cost.credits once a usage item's period is
// closed, so today's credits do not exist yet and would plot as a near-zero bar.
export const usageRangeFor = (key: UsageRangeKey, now: Date): { from: Date; to: Date; granularity: Granularity } => {
  const to = startOfUTCDay(now);
  switch (key) {
    case "MONTH_1":
      return { from: subtractCalendar(to, 1, "month"), to, granularity: Granularity.GRANULARITY_DAY };
    case "MONTH_3":
      return { from: subtractCalendar(to, 3, "month"), to, granularity: Granularity.GRANULARITY_DAY };
    case "YEAR_1":
      return { from: subtractCalendar(to, 1, "year"), to, granularity: Granularity.GRANULARITY_MONTH };
  }
};

// formatBucketLabel renders a bucket's period start for a chart axis, in UTC.
export const formatBucketLabel = (periodStart: Date, granularity: Granularity): string => {
  const iso = periodStart.toISOString();
  return granularity === Granularity.GRANULARITY_MONTH ? iso.slice(0, 7) : iso.slice(0, 10);
};

// periodBoundsFor is the inverse of formatBucketLabel: given a label exactly
// as formatBucketLabel produced it (either "YYYY-MM-DD" or "YYYY-MM") and the
// granularity it was formatted under, returns the UTC period it covers as a
// [from, to) range. Used to re-fetch a finer-grained breakdown scoped to a
// single bucket the viewer clicked on in the chart, rather than the whole
// selected range.
export const periodBoundsFor = (label: string, granularity: Granularity): { from: Date; to: Date } => {
  const [year, month, day] = label.split("-").map(Number);
  if (granularity === Granularity.GRANULARITY_MONTH) {
    return { from: new Date(Date.UTC(year, month - 1, 1)), to: new Date(Date.UTC(year, month, 1)) };
  }
  return { from: new Date(Date.UTC(year, month - 1, day)), to: new Date(Date.UTC(year, month - 1, day + 1)) };
};

// clampPeriodToRange intersects a clicked bucket's bounds (from
// periodBoundsFor) with the selected range's own [from, to) window.
//
// periodBoundsFor returns a WHOLE calendar day or month, but on the YEAR_1
// range (monthly buckets) the first and last plotted buckets are only
// PARTIAL calendar months - the range itself starts/ends mid-month. Without
// this clamp, clicking either edge bucket would request the entire calendar
// month: the breakdown totals would disagree with the partial bar the viewer
// actually clicked, and clicking the last bucket would request past `to`
// into today and the future, breaking usageRangeFor's invariant that today
// is deliberately excluded because its usage is not priced yet.
//
// A no-op for DAY granularity: a clicked day is always fully inside the
// range it was clicked from, so it is already within [range.from, range.to).
export const clampPeriodToRange = (period: { from: Date; to: Date }, range: { from: Date; to: Date }): { from: Date; to: Date } => ({
  from: period.from < range.from ? range.from : period.from,
  to: period.to > range.to ? range.to : period.to,
});
